/**
 * @documental
 * @export TabSet
 * @import
 * import { TabSet } from "capitalrx-components-2.0/lib/general";
 */

import {
  FC,
  useState,
  useCallback,
  useEffect,
  MouseEvent,
  TouchEvent,
  FocusEvent,
  UIEvent,
  useRef,
  useLayoutEffect,
  ReactNode,
} from "react";
import classNames from "classnames";
import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { withSubComponents } from "../../utilities/withSubComponents";
import { getOffsetRelativeTo } from "../../utilities/getOffsetRelativeTo";
import { doFrameLoop } from "../../utilities/doFrameLoop";
import { Tab } from "./Tab";
import { TooltipProps } from "../Tooltip";
import { SelectedTabChangeHandler } from "./SelectedTabChangeHandler";
import { InternalTab } from "./InternalTab";
import { SpacerTab } from "./SpacerTab";
import { getSelectedId, getSelectedTabElement, getTab, getTabs, raiseTransition, scrollToElementIfNotInView } from "./helpers";
import styles from "./styles.module.scss";

/**
 * The tab set component properties.
 */
export interface TabSetProps extends BaseProps {
  /**
   * The ID of the currently selected tab. If not specified the ID of the first non-disabled tab is used. If `null`
   * is specified then no tabs are selected.
   */
  readonly selectedTab?: string | null;

  /**
   * Indicates whether the tabs are disabled. Disabled tabs does not prevent tabs being selected through the `selectedTab`
   * property.
   */
  readonly disabled?: boolean;

  /**
   * Indicates whether tabs should span evenly across the entire tabs component area. If not set, defaults to false.
   */
  readonly spanTabsAcross?: boolean;

  /**
   * The tooltip appearance. If not specified defaults to "dark".
   */
  readonly tooltipAppearance?: TooltipProps["appearance"];

  /**
   * The tooltip alignment. If not specifies defaults to "cursor".
   */
  readonly tooltipAlignment?: TooltipProps["alignment"];

  /**
   * The tabs to display.
   */
  readonly children?: ReactNode;

  /**
   * Called when the selected tab is to be changed.
   * <br>
   * <br>
   * **NOTE:** When using asynchronous tabs, do not use this function to update other UI elements in response to
   * the tab change as this may lead to unexpected behaviour. Instead, you should use the `TabSet.Tab.onSelected`
   * or `TabSet.onSelectedTabChangeComplete` events.
   */
  readonly onSelectedTabChange?: SelectedTabChangeHandler;

  /**
   * Called when the selected tab has been successfully changed. Unlike `onSelectedTabChange` this event fires
   * only once the `TabSet` has succesfully switched to the requested tab.
   */
  readonly onSelectedTabChangeComplete?: SelectedTabChangeHandler;
}

/**
 * The tabs component sub components.
 */
export interface TabSetSubComponents {
  /**
   * Represents a single tab which can be placed within the TabSet component.
   */
  readonly Tab: typeof Tab;
}

/**
 * Shows a set of selectable tabs, allowing each tab to be clicked on and for the currently selected tab to be highlighted.
 */
export const TabSet = withSubComponents<FC<TabSetProps>, TabSetSubComponents>(
  {
    Tab,
  },
  (props) => {
    const {
      children,
      spanTabsAcross = false,
      disabled = false,
      tooltipAlignment,
      tooltipAppearance,
      onSelectedTabChange,
      onSelectedTabChangeComplete,
    } = props;
    const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
      className: [
        styles.TabSet,
        "tab-set",
        {
          "span-across": spanTabsAcross,
          disabled: disabled,
        },
      ],
      testIdPrefix: "tab-set",
    });

    const scrollAreaRef = useRef<HTMLDivElement | null>(null);
    const itemsRef = useRef<HTMLUListElement | null>(null);
    const indicatorRef = useRef<HTMLDivElement | null>(null);

    const [tabStates, setTabStates] = useState<Readonly<Record<string, "busy" | "error">>>({});
    const [transitionIndicator, setTransitionAnimateIndicator] = useState(false);
    const [leftOverflowIndicator, showLeftOverflowIndicator] = useState(false);
    const [rightOverflowIndicator, showRightOverflowIndicator] = useState(false);

    const tabs = getTabs(children);
    const selectedId = getSelectedId(props.selectedTab, tabs);
    const adjustedSelectedId = (selectedId === null && tabs.length > 0) ? tabs[0].id : selectedId;

    const [currentId, setCurrentSelectedTab] = useState<string | null>(null);

    // **** Begin section for optimization of memoized callbacks
    const beforeUnselectPromiseRef = useRef<Promise<boolean> | null>(null);

    const currentIdRef = useRef<string | null>(null);
    useEffect(() => {
      currentIdRef.current = currentId;
    }, [currentId]);

    const onSelectedTabChangeRef = useRef<SelectedTabChangeHandler | undefined>(onSelectedTabChange);
    useEffect(() => {
      onSelectedTabChangeRef.current = onSelectedTabChange;
    }, [onSelectedTabChange]);

    // **** End section for optimization of memoized callback.

    useEffect(() => {
      const { current: currentId } = currentIdRef;

      let cancelled = false;

      async function doTabTransition(): Promise<void> {
        const { current: onSelectedTabChange } = onSelectedTabChangeRef;

        setTabStates(beforeUnselectPromiseRef.current !== null && currentId !== null ? { [currentId]: "busy" } : {});

        const onBusy = (id: string) => {
          setTabStates((ids) => ({ ...ids, [id]: "busy" }));
        };

        const onError = (id: string) => {
          setTabStates((ids) => (ids[id] === "busy" ? { ...ids, [id]: "error" } : ids));
        };

        const onDone = (id: string) => {
          setTabStates((ids) => {
            if (ids[id] === "busy") {
              const { [id]: _, ...rest } = ids;
              return rest;
            }

            return ids;
          });
        };

        if (beforeUnselectPromiseRef.current === null) {
          const currentTab = getTab(tabs, currentId);
          beforeUnselectPromiseRef.current = raiseTransition(currentId, currentTab?.onBeforeUnselect, onBusy, onError, onDone);
        }

        const unselectSuccess = await beforeUnselectPromiseRef.current;
        beforeUnselectPromiseRef.current = null;
        if (cancelled) return;
        if (!unselectSuccess) return onSelectedTabChange?.(currentId);

        const nextTab = getTab(tabs, adjustedSelectedId);
        const selectSuccess = await raiseTransition(adjustedSelectedId, nextTab?.onBeforeSelect, onBusy, onError, onDone);
        if (cancelled) return;

        if (selectedId === null && adjustedSelectedId !== null) return onSelectedTabChange?.((selectSuccess) ? adjustedSelectedId : null);
        else if (!selectSuccess) return onSelectedTabChange?.(currentId);

        if (adjustedSelectedId !== currentId && currentId !== null && adjustedSelectedId !== null) setTransitionAnimateIndicator(true);

        setCurrentSelectedTab(adjustedSelectedId);
      }

      // if trying to move to a disabled tab, throw back the tab change.

      if (adjustedSelectedId !== currentId) {
        doTabTransition();
      }

      return () => {
        cancelled = true;
      };
    }, [selectedId, adjustedSelectedId]);

    // When the tab we're actually on changes, raise the select/unselect events.
    useEffect(() => {
      const tab = getTab(tabs, currentId);
      tab?.onSelect?.(tab.id);
      onSelectedTabChangeComplete?.(tab ? tab.id : null);

      return () => {
        tab?.onUnselect?.(tab.id);
      };
    }, [currentId]);

    // When the tab we're actually on changes, after we've committed DOM changes, scroll to the tab.
    useLayoutEffect(() => {
      const { current: items } = itemsRef;
      const { current: scrollArea } = scrollAreaRef;
      if (!items || !scrollArea) return;

      const tabElement = getSelectedTabElement(Array.from(items.childNodes));
      if (tabElement) scrollToElementIfNotInView(scrollArea, tabElement);
    }, [currentId]);

    const tabTransitionHandler = useCallback(
      (ev: UIEvent<HTMLAnchorElement>) => {
        if (adjustedSelectedId === null) return;

        const { currentTarget } = ev;
        const id = currentTarget.getAttribute("data-tabid") as string;
        if (id === null) return;
        if (id === adjustedSelectedId) return;
        const tab = getTab(tabs, id);
        if (tab?.disabled || disabled) return;

        onSelectedTabChange?.(id);
      },
      [adjustedSelectedId, onSelectedTabChange],
    );

    const tabFocusHandler = useCallback((ev: FocusEvent<HTMLAnchorElement>) => {
      const { current: scrollArea } = scrollAreaRef;
      if (!scrollArea) return;
      scrollToElementIfNotInView(scrollArea, ev.currentTarget);
    }, []);

    const tabClickHandler = useCallback(
      (ev: MouseEvent<HTMLAnchorElement>) => {
        ev.preventDefault();
        if (ev.button !== 0) return;
        tabTransitionHandler(ev);
      },
      [tabTransitionHandler],
    );

    const tabMouseDownHandler = useCallback(
      (ev: MouseEvent<HTMLAnchorElement>) => {
        ev.currentTarget.focus();
        ev.preventDefault();
        if (ev.button !== 0) return;
        tabTransitionHandler(ev);
      },
      [tabTransitionHandler],
    );

    const tabTouchStartHandler = useCallback(
      (ev: TouchEvent<HTMLAnchorElement>) => {
        tabTransitionHandler(ev);
      },
      [tabTransitionHandler],
    );

    const indicatorTransitionEndHandler = useCallback(() => {
      setTransitionAnimateIndicator(false);
    }, []);

    // Runs a looping process at 15fps that updates the indicator position and size
    useEffect(() => {
      return doFrameLoop((): void => {
        const { current: items } = itemsRef;
        if (!items) return;

        const { current: indicator } = indicatorRef;
        const { current: scrollArea } = scrollAreaRef;
        if (!indicator || !scrollArea) return;

        const selectedTab = getSelectedTabElement(Array.from(items.childNodes));
        if (!selectedTab) return;

        const clientArea = selectedTab.querySelector<HTMLSpanElement>(".client-area");
        if (!clientArea) return;

        const { left: clientAreaLeft } = getOffsetRelativeTo(clientArea, scrollArea);
        const { width: clientAreaWidth } = clientArea.getBoundingClientRect();

        const transformStyle = `translate(${scrollArea.scrollLeft + clientAreaLeft}px, 0) scaleX(${clientAreaWidth})`;
        indicator.style.transform = transformStyle;

        showLeftOverflowIndicator(scrollArea.scrollLeft > 0);
        showRightOverflowIndicator(scrollArea.scrollLeft < scrollArea.scrollWidth - scrollArea.offsetWidth);
      }, 4);
    }, []);

    return (
      <div {...baseHtmlProps} aria-label="Tabs" role="tablist" aria-orientation="horizontal">
        <div ref={scrollAreaRef} className={classNames(styles.ScrollArea, "scroll-area")}>
          <ul role="presentation" className={classNames(styles.Items, "items")} ref={itemsRef}>
            {(!tabs || tabs?.length === 0) && <SpacerTab />}
            {tabs?.map(({ id, children, tooltip, disabled: tabDisabled }) => (
              <InternalTab
                key={id}
                id={id}
                parentTestId={baseHtmlProps["data-testid"]}
                testId={id}
                disabled={disabled || tabDisabled}
                tooltip={tooltip}
                tooltipAlignment={tooltipAlignment}
                tooltipAppearance={tooltipAppearance}
                isBusy={tabStates[id] === "busy"}
                hasError={tabStates[id] === "error"}
                isSelected={id === currentId}
                onFocus={tabFocusHandler}
                onClick={tabClickHandler}
                onMouseDown={tabMouseDownHandler}
                onTouchStart={tabTouchStartHandler}
              >
                {children}
              </InternalTab>
            ))}
          </ul>
          <div
            ref={indicatorRef}
            className={classNames(styles.Indicator, "indicator", {
              transitioning: transitionIndicator,
              hidden: !tabs || tabs.length === 0 || currentId === null || (tabs.some((tab) => tabStates[tab.id] === "busy")),
            })}
            aria-hidden="true"
            onTransitionEnd={indicatorTransitionEndHandler}
          />
        </div>
        <div aria-hidden="true" className={classNames(styles.LeftOverflowIndicator, "overflow-indicator left", { visible: leftOverflowIndicator })} />
        <div aria-hidden="true" className={classNames(styles.RightOverflowIndicator, "overflow-indicator right", { visible: rightOverflowIndicator })} />
      </div>
    );
  },
);
